/* =====================================================================
 * EmojiPicker — 슬랙 스타일 이모지 피커 (공통 컴포넌트)
 *
 * 카테고리 탭, 검색, 그리드 레이아웃
 * 에이전트 그룹채팅, DM 등에서 공통으로 사용
 * ===================================================================== */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { Search, X } from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  이모지 데이터                                                       */
/* ------------------------------------------------------------------ */

interface EmojiCategory {
  id: string;
  name: string;
  icon: string;
  emojis: string[];
}

const EMOJI_CATEGORIES: EmojiCategory[] = [
  {
    id: 'frequent',
    name: '자주 사용',
    icon: '🕐',
    emojis: ['👍', '❤️', '😂', '🔥', '👏', '😍', '🎉', '🙏', '💯', '✅', '👀', '💪', '😊', '🤔', '👌', '💜'],
  },
  {
    id: 'smileys',
    name: '스마일리 & 사람',
    icon: '😀',
    emojis: [
      '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '😉', '😊', '😇',
      '🥰', '😍', '🤩', '😘', '😗', '😚', '😙', '🥲', '😋', '😛', '😜', '🤪',
      '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🫡', '🤐', '🤨', '😐', '😑', '😶',
      '🫥', '😏', '😒', '🙄', '😬', '🤥', '😌', '😔', '😪', '🤤', '😴', '😷',
      '🤒', '🤕', '🤢', '🤮', '🥴', '😵', '🤯', '🥳', '🥸', '😎', '🤓', '🧐',
      '😕', '🫤', '😟', '🙁', '😮', '😯', '😲', '😳', '🥺', '🥹', '😦', '😧',
      '😨', '😰', '😥', '😢', '😭', '😱', '😖', '😣', '😞', '😓', '😩', '😫',
      '🥱', '😤', '😡', '😠', '🤬', '😈', '👿', '💀', '☠️', '💩', '🤡', '👹',
      '👺', '👻', '👽', '👾', '🤖', '😺', '😸', '😹', '😻', '😼', '😽', '🙀',
      '😿', '😾', '🙈', '🙉', '🙊',
    ],
  },
  {
    id: 'gestures',
    name: '손 & 제스처',
    icon: '👋',
    emojis: [
      '👋', '🤚', '🖐️', '✋', '🖖', '🫱', '🫲', '🫳', '🫴', '🫷', '🫸',
      '👌', '🤌', '🤏', '✌️', '🤞', '🫰', '🤟', '🤘', '🤙', '👈', '👉',
      '👆', '🖕', '👇', '☝️', '🫵', '👍', '👎', '✊', '👊', '🤛', '🤜',
      '👏', '🙌', '🫶', '👐', '🤲', '🤝', '🙏', '✍️', '💅', '🤳', '💪',
      '🦾', '🦿', '🦵', '🦶', '👂', '🦻', '👃', '🧠', '🫀', '🫁', '🦷',
      '🦴', '👀', '👁️', '👅', '👄', '🫦',
    ],
  },
  {
    id: 'animals',
    name: '동물 & 자연',
    icon: '🐶',
    emojis: [
      '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐻‍❄️', '🐨', '🐯', '🦁',
      '🐮', '🐷', '🐸', '🐵', '🙈', '🙉', '🙊', '🐒', '🐔', '🐧', '🐦', '🐤',
      '🐣', '🐥', '🦆', '🦅', '🦉', '🦇', '🐺', '🐗', '🐴', '🦄', '🐝', '🪱',
      '🐛', '🦋', '🐌', '🐞', '🐜', '🪰', '🪲', '🪳', '🦟', '🦗', '🕷️', '🦂',
      '🐢', '🐍', '🦎', '🦖', '🦕', '🐙', '🦑', '🪼', '🦐', '🦞', '🦀', '🐡',
      '🐠', '🐟', '🐬', '🐳', '🐋', '🦈', '🐊', '🐅', '🐆', '🦓', '🦍', '🦧',
      '🐘', '🦣', '🦛', '🦏', '🐪', '🐫', '🦒', '🦘', '🦬', '🐃', '🐂', '🐄',
      '🌸', '💐', '🌹', '🥀', '🌺', '🌻', '🌼', '🌷', '🪻', '🌱', '🪴', '🌲',
      '🌳', '🌴', '🌵', '🌾', '🌿', '☘️', '🍀', '🍁', '🍂', '🍃', '🪹', '🪺',
    ],
  },
  {
    id: 'food',
    name: '식음료',
    icon: '🍔',
    emojis: [
      '🍇', '🍈', '🍉', '🍊', '🍋', '🍌', '🍍', '🥭', '🍎', '🍏', '🍐', '🍑',
      '🍒', '🍓', '🫐', '🥝', '🍅', '🫒', '🥥', '🥑', '🍆', '🥔', '🥕', '🌽',
      '🌶️', '🫑', '🥒', '🥬', '🥦', '🧄', '🧅', '🥜', '🫘', '🌰', '🫚', '🫛',
      '🍞', '🥐', '🥖', '🫓', '🥨', '🥯', '🥞', '🧇', '🧀', '🍖', '🍗', '🥩',
      '🥓', '🍔', '🍟', '🍕', '🌭', '🥪', '🌮', '🌯', '🫔', '🥙', '🧆', '🥚',
      '🍳', '🥘', '🍲', '🫕', '🥣', '🥗', '🍿', '🧈', '🧂', '🥫', '🍱', '🍘',
      '🍙', '🍚', '🍛', '🍜', '🍝', '🍠', '🍢', '🍣', '🍤', '🍥', '🥮', '🍡',
      '🥟', '🥠', '🥡', '🦀', '🦞', '🦐', '🦑', '🦪', '🍦', '🍧', '🍨', '🍩',
      '🍪', '🎂', '🍰', '🧁', '🥧', '🍫', '🍬', '🍭', '🍮', '🍯', '🍼', '🥛',
      '☕', '🫖', '🍵', '🍶', '🍾', '🍷', '🍸', '🍹', '🍺', '🍻', '🥂', '🥃',
    ],
  },
  {
    id: 'activities',
    name: '활동',
    icon: '⚽',
    emojis: [
      '⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🥏', '🎱', '🪀', '🏓',
      '🏸', '🏒', '🏑', '🥍', '🏏', '🪃', '🥅', '⛳', '🪁', '🏹', '🎣', '🤿',
      '🥊', '🥋', '🎽', '🛹', '🛼', '🛷', '⛸️', '🥌', '🎿', '⛷️', '🏂', '🪂',
      '🏋️', '🤸', '🤼', '🤺', '⛹️', '🧗', '🚴', '🚵', '🏇', '🎪', '🎭', '🎨',
      '🎬', '🎤', '🎧', '🎼', '🎹', '🥁', '🪘', '🎷', '🎺', '🪗', '🎸', '🎻',
      '🎲', '♟️', '🎯', '🎳', '🎮', '🕹️', '🧩', '🎰', '🎗️', '🏆', '🏅', '🥇',
      '🥈', '🥉', '🎖️', '🎫', '🎟️', '🎉', '🎊',
    ],
  },
  {
    id: 'travel',
    name: '여행 & 장소',
    icon: '✈️',
    emojis: [
      '🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐', '🛻', '🚚',
      '🚛', '🚜', '🛵', '🏍️', '🛺', '🚲', '🛴', '🚨', '🚔', '🚍', '🚘', '🚖',
      '✈️', '🛫', '🛬', '🛩️', '💺', '🚀', '🛸', '🚁', '🛶', '⛵', '🚤', '🛥️',
      '🛳️', '⛴️', '🚢', '🗼', '🏰', '🏯', '🏟️', '🎡', '🎢', '🎠', '⛲', '⛱️',
      '🏖️', '🏝️', '🏜️', '🌋', '⛰️', '🏔️', '🗻', '🏕️', '🛖', '🏠', '🏡', '🏘️',
      '🏚️', '🏗️', '🏢', '🏣', '🏤', '🏥', '🏦', '🏨', '🏩', '🏪', '🏫', '🏬',
      '🌅', '🌄', '🌠', '🎇', '🎆', '🌇', '🌆', '🏙️', '🌃', '🌌', '🌉', '🌁',
    ],
  },
  {
    id: 'objects',
    name: '사물',
    icon: '💡',
    emojis: [
      '⌚', '📱', '📲', '💻', '⌨️', '🖥️', '🖨️', '🖱️', '🖲️', '🕹️', '🗜️', '💽',
      '💾', '💿', '📀', '📼', '📷', '📸', '📹', '🎥', '📽️', '🎞️', '📞', '☎️',
      '📟', '📠', '📺', '📻', '🎙️', '🎚️', '🎛️', '🧭', '⏱️', '⏲️', '⏰', '🕰️',
      '💡', '🔦', '🕯️', '🪔', '🧯', '💰', '💴', '💵', '💶', '💷', '🪙', '💸',
      '💳', '🧾', '✉️', '📧', '📨', '📩', '📤', '📥', '📦', '📫', '📪', '📬',
      '📭', '📮', '🗳️', '✏️', '✒️', '🖋️', '🖊️', '🖌️', '🖍️', '📝', '📁', '📂',
      '🗂️', '📅', '📆', '📇', '📈', '📉', '📊', '📋', '📌', '📍', '📎', '🖇️',
      '📏', '📐', '✂️', '🗃️', '🗄️', '🗑️', '🔒', '🔓', '🔏', '🔐', '🔑', '🗝️',
    ],
  },
  {
    id: 'symbols',
    name: '기호',
    icon: '❤️',
    emojis: [
      '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❤️‍🔥', '❤️‍🩹',
      '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '☮️', '✝️', '☪️',
      '🕉️', '☸️', '✡️', '🔯', '🕎', '☯️', '☦️', '🛐', '⛎', '♈', '♉', '♊',
      '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓', '🆔', '⚛️', '🉑',
      '☢️', '☣️', '📴', '📳', '🈶', '🈚', '🈸', '🈺', '🈷️', '✴️', '🆚', '💮',
      '🉐', '㊙️', '㊗️', '🈴', '🈵', '🈹', '🈲', '🅰️', '🅱️', '🆎', '🆑', '🅾️',
      '🆘', '❌', '⭕', '🛑', '⛔', '📛', '🚫', '💯', '💢', '♨️', '🚷', '🚯',
      '🚳', '🚱', '🔞', '📵', '🚭', '❗', '❕', '❓', '❔', '‼️', '⁉️', '🔅',
      '🔆', '〽️', '⚠️', '🚸', '🔱', '⚜️', '🔰', '♻️', '✅', '🈯', '💹', '❇️',
      '✳️', '❎', '🌐', '💠', 'Ⓜ️', '🌀', '💤', '🏧', '🚾', '♿', '🅿️', '🛗',
    ],
  },
  {
    id: 'flags',
    name: '깃발',
    icon: '🏁',
    emojis: [
      '🏁', '🚩', '🎌', '🏴', '🏳️', '🏳️‍🌈', '🏳️‍⚧️', '🏴‍☠️',
      '🇰🇷', '🇺🇸', '🇯🇵', '🇨🇳', '🇬🇧', '🇫🇷', '🇩🇪', '🇮🇹',
      '🇪🇸', '🇧🇷', '🇦🇺', '🇨🇦', '🇮🇳', '🇷🇺', '🇲🇽', '🇹🇭',
      '🇻🇳', '🇵🇭', '🇸🇬', '🇮🇩', '🇲🇾', '🇹🇼', '🇭🇰', '🇳🇿',
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
  onClose: () => void;
  anchorRect?: DOMRect | null;
  /** 오른쪽 정렬 여부 (유저 메시지일 때) */
  alignRight?: boolean;
}

export const EmojiPicker: React.FC<EmojiPickerProps> = ({
  onSelect,
  onClose,
  anchorRect,
  alignRight = false,
}) => {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('frequent');
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const filteredCategories = useMemo(() => {
    if (!search.trim()) return EMOJI_CATEGORIES;

    const q = search.trim().toLowerCase();
    const result: EmojiCategory[] = [];

    for (const cat of EMOJI_CATEGORIES) {
      const matched = cat.emojis.filter((e) => e.includes(q));
      if (matched.length > 0) {
        result.push({ ...cat, emojis: matched });
      }
    }

    if (result.length > 0) {
      return [
        {
          id: 'search_results',
          name: `"${search}" 검색 결과`,
          icon: '🔍',
          emojis: result.flatMap((c) => c.emojis),
        },
      ];
    }
    return [];
  }, [search]);

  const handleCategoryClick = useCallback((catId: string) => {
    setActiveCategory(catId);
    setSearch('');
    const el = categoryRefs.current[catId];
    if (el && scrollRef.current) {
      const containerTop = scrollRef.current.getBoundingClientRect().top;
      const elTop = el.getBoundingClientRect().top;
      scrollRef.current.scrollTop += elTop - containerTop;
    }
  }, []);

  const handleScroll = useCallback(() => {
    if (search.trim()) return;
    const container = scrollRef.current;
    if (!container) return;

    const containerTop = container.getBoundingClientRect().top;

    for (const cat of EMOJI_CATEGORIES) {
      const el = categoryRefs.current[cat.id];
      if (!el) continue;
      const elTop = el.getBoundingClientRect().top - containerTop;
      if (elTop <= 8) {
        setActiveCategory(cat.id);
      }
    }
  }, [search]);

  const style = useMemo(() => {
    if (!anchorRect) return {};
    const pickerWidth = 352;
    const pickerHeight = 420;
    const pad = 8;

    let top = anchorRect.bottom + pad;
    let left = alignRight ? anchorRect.right - pickerWidth : anchorRect.left;

    if (top + pickerHeight > window.innerHeight - pad) {
      top = anchorRect.top - pickerHeight - pad;
    }
    if (left < pad) left = pad;
    if (left + pickerWidth > window.innerWidth - pad) {
      left = window.innerWidth - pickerWidth - pad;
    }

    return { top, left };
  }, [anchorRect, alignRight]);

  return (
    <Overlay>
      <Container ref={containerRef} style={style}>
        <CategoryTabs>
          {EMOJI_CATEGORIES.map((cat) => (
            <CategoryTab
              key={cat.id}
              $active={activeCategory === cat.id && !search.trim()}
              onClick={() => handleCategoryClick(cat.id)}
              title={cat.name}
            >
              {cat.icon}
            </CategoryTab>
          ))}
        </CategoryTabs>

        <SearchWrap>
          <SearchIcon>
            <Search size={14} />
          </SearchIcon>
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="이모티콘 검색"
            autoFocus
          />
          {search && (
            <ClearBtn onClick={() => setSearch('')}>
              <X size={14} />
            </ClearBtn>
          )}
        </SearchWrap>

        <EmojiScroll ref={scrollRef} onScroll={handleScroll}>
          {filteredCategories.length === 0 && (
            <EmptyMsg>검색 결과가 없습니다</EmptyMsg>
          )}
          {filteredCategories.map((cat) => (
            <CategorySection
              key={cat.id}
              ref={(el) => { categoryRefs.current[cat.id] = el; }}
            >
              <CategoryLabel>{cat.name}</CategoryLabel>
              <EmojiGrid>
                {cat.emojis.map((emoji, i) => (
                  <EmojiCell
                    key={`${emoji}-${i}`}
                    onClick={() => onSelect(emoji)}
                    title={emoji}
                  >
                    {emoji}
                  </EmojiCell>
                ))}
              </EmojiGrid>
            </CategorySection>
          ))}
        </EmojiScroll>
      </Container>
    </Overlay>
  );
};

/* ---- Styled ---- */

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(4px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
`;

const Container = styled.div`
  position: fixed;
  width: 352px;
  height: 420px;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18), 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  animation: ${slideIn} 0.15s ease;
  z-index: 10000;
`;

const CategoryTabs = styled.div`
  display: flex;
  gap: 0;
  padding: 6px 8px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  flex-shrink: 0;
`;

const CategoryTab = styled.button<{ $active: boolean }>`
  flex: 1;
  min-width: 0;
  padding: 6px 0 8px;
  border: none;
  background: transparent;
  font-size: 16px;
  cursor: pointer;
  border-bottom: 2px solid
    ${({ $active, theme }) => ($active ? theme.colors.accent : 'transparent')};
  opacity: ${({ $active }) => ($active ? 1 : 0.5)};
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 1;
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'};
  }
`;

const SearchWrap = styled.div`
  position: relative;
  margin: 8px 10px 4px;
  flex-shrink: 0;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.textMuted};
  display: flex;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 7px 32px 7px 30px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 8px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s ease;

  &:focus {
    border-color: ${({ theme }) => theme.colors.accent};
  }
  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const ClearBtn = styled.button`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
  }
`;

const EmojiScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 6px 8px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.scrollbar};
    border-radius: 2px;
  }
`;

const CategorySection = styled.div`
  padding-top: 4px;
`;

const CategoryLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
  padding: 6px 6px 4px;
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.colors.bgPrimary};
  z-index: 1;
`;

const EmojiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(9, 1fr);
  gap: 1px;
`;

const EmojiCell = styled.button`
  width: 100%;
  aspect-ratio: 1;
  border: none;
  border-radius: 6px;
  background: transparent;
  font-size: 22px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.1s ease, transform 0.1s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'};
    transform: scale(1.15);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const EmptyMsg = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 120px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 13px;
`;
